import { useEffect, useMemo, useState } from "react";

import {
  Select,
  Checkbox,
  Modal,
  Button,
  DatePicker,
  InfoPanel,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { DEFAULT_PERSON_DETAILS } from "./constants";
import {
  buildOibPersonDataFromRequestChild,
  buildOibPersonDataFromRequestParent,
  createEmptyPersonIdentification,
} from "./utils";
import { OibIdentitySection } from "../oib";
import {
  createRequest,
  getPersonServiceData,
  getInitialQuestionnaireExport,
} from "../../api/Request/RequestApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  AddressSelect,
  InstitutionSelect,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import { ValidationFailure, ServiceResultStatus } from "../../models";
import {
  ParentChildDetails,
  PersonAddress,
  PersonDetails,
  RequestType,
} from "../../models/Request";
import { Address } from "../../models/Shared/Address";
import { PersonIdentification } from "../../models/Shared/PersonIdentification";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetGenderList,
} from "../../queries/codeBookQuery";
import { requestEditUrl } from "../../routes/routes";
import {
  resetIsEdited,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";
import { MsgUtils } from "../../utils";

export function RequestCreate() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  const dispatch = useAppDispatch();
  const oibRegex = /^\d{11}$/;

  const { data: alternativeCodeReasons } =
    useGetAlternativeCodeReasonListQuery();
  const { data: alternativeCodeTypes } = useGetAlternativeCodeTypeListQuery();
  const { data: genders } = useGetGenderList();

  const [item, setItem] = useState<ParentChildDetails>({});
  const [requestType, setRequestType] = useState<RequestType>(
    RequestType.RegularEnrollment
  );
  const [childWithoutOib, setChildWithoutOib] = useState<boolean>(false);
  const [showChildOibAlert, setShowChildOibAlert] = useState<
    string | undefined
  >();
  const [showChildOibSuccess, setShowChildOibSuccess] = useState<
    string | undefined
  >();
  const [showParentOibAlert, setShowParentOibAlert] = useState<
    string | undefined
  >();
  const [showParentOibSuccess, setShowParentOibSuccess] = useState<
    string | undefined
  >();
  const [parentWithoutOib, setParentWithoutOib] = useState(false);
  const [institutionId, setInstitutionId] = useState(0);
  const [isChild, setIsChild] = useState<boolean | undefined>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [isChildChecked, setIsChildChecked] = useState(false);
  const [isParentChecked, setIsParentChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // This may be required where the parent/child relationship can't be established automatically.
  // Display something to the user and force them to accept the condition.
  // This is not currently stored anywhere. It should be and a document should be
  // forced with the parent.
  const [force, setForce] = useState(false);
  useEffect(() => {
    (async () => {
      if (
        auth.currentRole?.claimType === ClaimType.Institution &&
        auth.currentRole?.locations &&
        auth.currentRole.locations.length > 0
      ) {
        setInstitutionId(auth.currentRole.locations[0]);
      }
    })();
  }, [auth.currentRole?.locations]);

  const isChildPersonValid = (): boolean => {
    const childIsValid: boolean =
      item.child !== undefined &&
      item.child.person != undefined &&
      ((item.child.person?.oib != undefined &&
        oibRegex.test(item.child.person.oib) &&
        isChildChecked) ||
        (item.child.person?.alternativeDocumentNumber != undefined &&
          item.child.person.alternativeDocumentNumber.length > 0 &&
          item.child.person?.alternativeDocumentReasonId != undefined &&
          item.child.person?.alternativeDocumentTypeId != undefined));
    return childIsValid;
  };
  const isParentPersonValid = (): boolean => {
    const parentIsValid: boolean =
      item.parent !== undefined &&
      item.parent.person != undefined &&
      ((item.parent.person?.oib != undefined &&
        oibRegex.test(item.parent.person.oib) &&
        isParentChecked) ||
        (item.parent.person?.alternativeDocumentNumber != undefined &&
          item.parent.person.alternativeDocumentNumber.length > 0 &&
          item.parent.person?.alternativeDocumentReasonId != undefined &&
          item.parent.person?.alternativeDocumentTypeId != undefined));
    return parentIsValid;
  };
  const isParentChildValid = (): boolean => {
    const childIsValid: boolean =
      item.child !== undefined &&
      isChildPersonValid() &&
      item.child.firstName !== undefined &&
      item.child.firstName.length > 0 &&
      item.child.lastName !== undefined &&
      item.child.lastName.length > 0 &&
      item.child.residence !== undefined &&
      (item.child.residence?.permanentAddress !== undefined ||
        item.child.residence?.temporaryAddress !== undefined);
    const parentIsValid: boolean =
      item.parent !== undefined &&
      isParentPersonValid() &&
      item.parent.firstName !== undefined &&
      item.parent.firstName.length > 0 &&
      item.parent.lastName !== undefined &&
      item.parent.lastName.length > 0 &&
      item.parent.residence !== undefined &&
      (item.parent.residence?.permanentAddress !== undefined ||
        item.parent.residence?.temporaryAddress !== undefined);
    return childIsValid && parentIsValid;
  };

  const setParentChild = (item: ParentChildDetails) => {
    setItem(item);
    dispatch(setIsEdited());
  };
  const handlePrimaryInstitutionChange = (val: number) => {
    setInstitutionId(val);
  };
  const handleRequestTypeChange = (val: number) => {
    if (!isNaN(val)) setRequestType(val);
  };

  const handleChildNoOibChange = (isNoOib: boolean) => {
    const newChild: PersonIdentification = createEmptyPersonIdentification(
      item.child?.person
    );
    const newChildDetails: PersonDetails = isNoOib
      ? { ...DEFAULT_PERSON_DETAILS, person: newChild }
      : {
          ...(item.child ?? DEFAULT_PERSON_DETAILS),
          person: newChild,
        };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
    setChildWithoutOib(isNoOib);
    setShowChildOibAlert(undefined);
    setShowChildOibSuccess(undefined);
    setErrors(undefined);
  };

  const handleChildOibCheckClick = async () => {
    if (item.child?.person.oib && oibRegex.test(item.child.person.oib)) {
      const result = await getPersonServiceData(item.child.person);
      if (
        result &&
        (result.status & ServiceResultStatus.Success) ===
          ServiceResultStatus.Success &&
        result.item
      ) {
        const newItem: ParentChildDetails = {
          ...item,
          child: result.item,
        };
        setItem(newItem);
        setIsChildChecked(true);
        setShowChildOibSuccess(MsgUtils.getOibErrorMessage(result.status));
      } else {
        setShowChildOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };
  const handleEntityWithoutOibChange = () => {
    // This is called from the Modal if the user clicks confirm.
    // As we need to show a modal allowing confirm/cancel, we store the item in state.
    if (isChild ?? false) {
      setChildWithoutOib(true);
      const newChild: PersonIdentification = createEmptyPersonIdentification(
        item.child?.person
      );
      const newChildDetails: PersonDetails = {
        ...(item.child ?? DEFAULT_PERSON_DETAILS),
        person: newChild,
      };
      const newItem: ParentChildDetails = {
        ...item,
        child: newChildDetails,
      };
      setParentChild(newItem);
    } else {
      setParentWithoutOib(true);
      const newParent: PersonIdentification = createEmptyPersonIdentification(
        item.parent?.person
      );
      const newParentDetails: PersonDetails = {
        ...(item.parent ?? DEFAULT_PERSON_DETAILS),
        person: newParent,
      };
      const newItem: ParentChildDetails = {
        ...item,
        parent: newParentDetails,
      };
      setParentChild(newItem);
    }
    setIsChild(undefined);
    setShowModal(false);
  };

  const handleChildNameChange = (
    val: string,
    field: "firstName" | "lastName"
  ) => {
    let newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
    };

    if (childWithoutOib) {
      newChildDetails[field] = val;
    } else {
      newChildDetails = {
        ...newChildDetails,
        firstName: field === "firstName" ? val : item.child?.firstName ?? "",
        lastName: field === "lastName" ? val : item.child?.lastName ?? "",
        person: {
          ...(item.child?.person ?? DEFAULT_PERSON_DETAILS.person),
          [field]: val,
        },
      };
    }

    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };

  const handleChildOibChange = (val: string) => {
    const newChild: PersonIdentification = createEmptyPersonIdentification(
      item.child?.person,
      val
    );
    const newChildDetails: PersonDetails = {
      ...DEFAULT_PERSON_DETAILS,
      person: newChild,
      firstName: item.child?.person.firstName ?? "",
      lastName: item.child?.person.lastName ?? "",
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
    setIsChildChecked(false);
    setShowChildOibAlert(undefined);
    setShowChildOibSuccess(undefined);
    setErrors(undefined);
  };
  const handleChildDocumentNumberChange = (val: string) => {
    const newChild: PersonIdentification = {
      ...createEmptyPersonIdentification(item.child?.person),
      alternativeDocumentNumber: val,
    };
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      person: newChild,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleChildDocumentTypeChange = (val: number) => {
    const newChild: PersonIdentification = {
      ...createEmptyPersonIdentification(item.child?.person),
      alternativeDocumentTypeId: !isNaN(val) ? val : null,
    };
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      person: newChild,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleChildDocumentReasonChange = (val: number) => {
    const newChild: PersonIdentification = {
      ...createEmptyPersonIdentification(item.child?.person),
      alternativeDocumentReasonId: !isNaN(val) ? val : null,
    };
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      person: newChild,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };

  const childPersonData = useMemo(
    () =>
      buildOibPersonDataFromRequestChild({
        child: item.child,
        alternativeCodeTypes,
        alternativeCodeReasons,
        handleChildDocumentNumberChange,
        handleChildDocumentTypeChange,
        handleChildDocumentReasonChange,
        handleChildNameChange,
      }),
    [
      item.child,
      alternativeCodeTypes,
      alternativeCodeReasons,
      handleChildDocumentNumberChange,
      handleChildDocumentTypeChange,
      handleChildDocumentReasonChange,
      handleChildNameChange,
    ]
  );

  const handleChildDateOfBirthChange = (val: Date) => {
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      dateOfBirth: val,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleChildGenderChange = (val: number) => {
    if (!isNaN(val)) {
      const newChildDetails: PersonDetails = {
        ...(item.child ?? DEFAULT_PERSON_DETAILS),
        genderId: val,
      };
      const newItem: ParentChildDetails = {
        ...item,
        child: newChildDetails,
      };
      setParentChild(newItem);
    }
  };
  const handleChildPermanentAddressChange = (address: Address) => {
    const newAddress: PersonAddress = {
      ...(item.child?.residence ?? { id: 0 }),
      permanentAddress: address?.houseNumberId ? address : undefined,
    };
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      residence: newAddress,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleChildTemporaryAddressChange = (address: Address) => {
    const newAddress: PersonAddress = {
      ...(item.child?.residence ?? { id: 0 }),
      temporaryAddress: address?.houseNumberId ? address : undefined,
    };
    const newChildDetails: PersonDetails = {
      ...(item.child ?? DEFAULT_PERSON_DETAILS),
      residence: newAddress,
    };
    const newItem: ParentChildDetails = {
      ...item,
      child: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleParentNoOibChange = (isNoOib: boolean) => {
    const newParent: PersonIdentification = createEmptyPersonIdentification(
      item.parent?.person
    );
    const newParentDetails: PersonDetails = isNoOib
      ? { ...DEFAULT_PERSON_DETAILS, person: newParent }
      : {
          ...(item.parent ?? DEFAULT_PERSON_DETAILS),
          person: newParent,
        };

    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
    setParentWithoutOib(isNoOib);
    setShowParentOibAlert(undefined);
    setShowParentOibSuccess(undefined);
    setErrors(undefined);
  };
  const handleParentOibCheckClick = async () => {
    if (item.parent?.person.oib && oibRegex.test(item.parent.person.oib)) {
      const result = await getPersonServiceData(item.parent.person);
      if (
        result &&
        (result.status & ServiceResultStatus.Success) ===
          ServiceResultStatus.Success &&
        result.item
      ) {
        const newItem: ParentChildDetails = {
          ...item,
          parent: result.item,
        };
        setItem(newItem);
        setIsParentChecked(true);
        setShowParentOibSuccess(MsgUtils.getOibErrorMessage(result.status));
      } else {
        setShowParentOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
      }
    }
  };

  const handleParentNameChange = (
    val: string,
    field: "firstName" | "lastName"
  ) => {
    let newParentDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
    };

    if (parentWithoutOib) {
      newParentDetails[field] = val;
    } else {
      newParentDetails = {
        ...newParentDetails,
        firstName: field === "firstName" ? val : item.parent?.firstName ?? "",
        lastName: field === "lastName" ? val : item.parent?.lastName ?? "",
        person: {
          ...(item.parent?.person ?? DEFAULT_PERSON_DETAILS.person),
          [field]: val,
        },
      };
    }
    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
  };

  const handleParentOibChange = (val: string) => {
    const newParent: PersonIdentification = createEmptyPersonIdentification(
      item.parent?.person,
      val
    );
    const newParentDetails: PersonDetails = {
      ...DEFAULT_PERSON_DETAILS,
      person: newParent,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
    setIsParentChecked(false);
    setShowParentOibAlert(undefined);
    setShowParentOibSuccess(undefined);
    setErrors(undefined);
  };
  const handleParentDocumentNumberChange = (val: string) => {
    const newParent: PersonIdentification = {
      ...createEmptyPersonIdentification(item.parent?.person),
      alternativeDocumentNumber: val,
    };
    const newParentDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      person: newParent,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
  };
  const handleParentDocumentTypeChange = (val: number) => {
    const newParent: PersonIdentification = {
      ...createEmptyPersonIdentification(item.parent?.person),
      alternativeDocumentTypeId: !isNaN(val) ? val : null,
    };
    const newParentDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      person: newParent,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
  };
  const handleParentDocumentReasonChange = (val: number) => {
    const newParent: PersonIdentification = {
      ...createEmptyPersonIdentification(item.parent?.person),
      alternativeDocumentReasonId: !isNaN(val) ? val : null,
    };
    const newParentDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      person: newParent,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newParentDetails,
    };
    setParentChild(newItem);
  };

  const parentPersonData = useMemo(
    () =>
      buildOibPersonDataFromRequestParent({
        parent: item.parent,
        alternativeCodeTypes,
        alternativeCodeReasons,
        handleParentDocumentNumberChange,
        handleParentDocumentTypeChange,
        handleParentDocumentReasonChange,
        handleParentNameChange,
      }),
    [
      item.parent,
      alternativeCodeTypes,
      alternativeCodeReasons,
      handleParentDocumentNumberChange,
      handleParentDocumentTypeChange,
      handleParentDocumentReasonChange,
      handleParentNameChange,
    ]
  );

  const handleParentDateOfBirthChange = (val: Date) => {
    const newChildDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      dateOfBirth: val,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleParentGenderChange = (val: number) => {
    if (!isNaN(val)) {
      const newChildDetails: PersonDetails = {
        ...(item.parent ?? DEFAULT_PERSON_DETAILS),
        genderId: val,
      };
      const newItem: ParentChildDetails = {
        ...item,
        parent: newChildDetails,
      };
      setParentChild(newItem);
    }
  };
  const handleParentPermanentAddressChange = (address: Address) => {
    const newAddress: PersonAddress = {
      ...(item.parent?.residence ?? { id: 0 }),
      permanentAddress: address?.houseNumberId ? address : undefined,
    };
    const newChildDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      residence: newAddress,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleParentTemporaryAddressChange = (address: Address) => {
    const newAddress: PersonAddress = {
      ...(item.parent?.residence ?? { id: 0 }),
      temporaryAddress: address?.houseNumberId ? address : undefined,
    };
    const newChildDetails: PersonDetails = {
      ...(item.parent ?? DEFAULT_PERSON_DETAILS),
      residence: newAddress,
    };
    const newItem: ParentChildDetails = {
      ...item,
      parent: newChildDetails,
    };
    setParentChild(newItem);
  };
  const handleGetQuestionnaireClicked = async () => {
    await getInitialQuestionnaireExport();
  };
  const handleSaveClicked2 = async () => {
    handleSaveClicked(requestType);
  };
  const handleSaveClicked = async (type: RequestType) => {
    const result = await createRequest(item, type, institutionId, force);
    if (result) {
      setErrors(result.validationFailures);
      if (!result.isValid) {
        if (result.result) {
          const pcd: ParentChildDetails = {
            child: result.result.childDetails,
            parent: result.result.applicantParentDetails,
          };
          setItem(pcd);
        }
      } else if (result.result) {
        dispatch(resetIsEdited());
        dispatch(setSaveOK());
        history.replace({
          pathname: requestEditUrl(result.result.id),
        });
      }
    }
  };

  const handleEntityWithoutOibCancel = () => {
    setIsChild(undefined);
    setShowModal(false);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("common.requestModel.createPageName")}
        errors={errors}
        showCustom={true}
      >
        <Button onClick={handleGetQuestionnaireClicked}>
          {t("common.requestModel.getQuestionnaire")}
        </Button>
      </ToolbarTop>
      <FormCard>
        <Row>
          <Col md={8}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={"institutionId"}
              value={institutionId}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentInstitution
              excludeHandicapped={true}
              excludePrivate={false}
              excludeSatellite={false}
              onChange={(e) =>
                handlePrimaryInstitutionChange(Number.parseInt(e.target.value))
              }
              errorText={
                errors?.find((e) => e.propertyName === "InstitutionId")?.message
              }
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("common.requestModel.requestType")}
              name={"requestType"}
              value={requestType}
              onChange={(e) =>
                handleRequestTypeChange(Number.parseInt(e.target.value))
              }
              selectData={codeBookToSelectData(
                MsgUtils.getEnumToCodeBook(RequestType, "requestType")
              )}
              errorText={
                errors?.find((e) => e.propertyName === "Type")?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard>
        <OibIdentitySection
          defaultIsNoOibChecked={childWithoutOib}
          onNoOibChange={handleChildNoOibChange}
          personData={childPersonData}
          isOibConnected={!!showChildOibSuccess}
          oibValue={item.child?.person?.oib}
          onOibChange={handleChildOibChange}
          oibErrorText={
            errors?.find((e) => e.propertyName === "ChildDetails.Person.OIB")
              ?.message
          }
          isOibReadonly={false}
          showConnectButton={true}
          onConnectClick={handleChildOibCheckClick}
          isConnected={false}
          alternativeCodeErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ChildDetails.Person.AlternativeDocumentNumber"
            )?.message
          }
          alternativeTypeErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ChildDetails.Person.AlternativeDocumentTypeId"
            )?.message
          }
          alternativeReasonErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ChildDetails.Person.AlternativeDocumentReasonId"
            )?.message
          }
          lastNameErrorText={
            errors?.find((e) => e.propertyName === "ChildDetails.LastName")
              ?.message
          }
          isLastNameReadonly={!!showChildOibSuccess}
          firstNameErrorText={
            errors?.find((e) => e.propertyName === "ChildDetails.FirstName")
              ?.message
          }
          isFirstNameReadonly={!!showChildOibSuccess}
        />
        <Row>
          <Col md={6}>
            {showChildOibAlert && (
              <InfoPanel
                color="danger"
                message=""
                dismissible={false}
                fullWidth
              >
                {showChildOibAlert}
              </InfoPanel>
            )}
            {showChildOibSuccess && (
              <InfoPanel
                color="success"
                message=""
                dismissible={false}
                fullWidth
              >
                {showChildOibSuccess}
              </InfoPanel>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <DatePicker
              label={t("common.schoolCandidateModel.dateOfBirth")}
              name="schoolCandidate.dateOfBirth"
              onChange={(e) =>
                handleChildDateOfBirthChange(new Date(e.target.value))
              }
              value={
                item.child?.dateOfBirth
                  ? new Date(item.child.dateOfBirth)
                  : null
              }
              minYear={2000}
              disabled={!childWithoutOib}
              // errorText={errors?.find(e => e.propertyName.startsWith("ChildDetails.DateOfBirth"))?.message}
            />
          </Col>
          <Col md={2}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              name="schoolCandidate.gender"
              value={item.child?.genderId}
              onChange={(e) =>
                handleChildGenderChange(Number.parseInt(e.target.value))
              }
              selectData={codeBookToSelectData(
                genders?.filter((v) => v.id !== 0)
              )}
              disabled={!childWithoutOib}
              errorText={
                errors?.find((e) => e.propertyName === "ChildDetails.GenderId")
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AddressSelect
              title={t("common.schoolCandidateModel.permanentAddress")}
              readOnly={false}
              disabled={!childWithoutOib}
              value={item.child?.residence?.permanentAddress}
              handleChange={handleChildPermanentAddressChange}
              errorText={
                errors?.find(
                  (e) =>
                    e.propertyName === "ChildDetails.Residence.PermanentAddress"
                )?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AddressSelect
              title={t("common.schoolCandidateModel.temporaryAddress")}
              readOnly={false}
              disabled={!childWithoutOib}
              value={item.child?.residence?.temporaryAddress}
              handleChange={handleChildTemporaryAddressChange}
              errorText={
                errors?.find(
                  (e) =>
                    e.propertyName === "ChildDetails.Residence.TemporaryAddress"
                )?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard>
        <OibIdentitySection
          defaultIsNoOibChecked={parentWithoutOib}
          onNoOibChange={handleParentNoOibChange}
          personData={parentPersonData}
          isOibConnected={!!showParentOibSuccess}
          oibValue={item.parent?.person?.oib}
          onOibChange={handleParentOibChange}
          oibErrorText={
            errors?.find((e) => e.propertyName === "ParentDetails.Person.OIB")
              ?.message
          }
          isOibReadonly={false}
          showConnectButton
          onConnectClick={handleParentOibCheckClick}
          isConnected={false}
          noOibCheckboxLabel={t("common.requestModel.parentWithoutOib")}
          oibLabel={t("common.requestModel.parentOib")}
          lastNameLabel={t("common.requestModel.parentLastName")}
          firstNameLabel={t("common.requestModel.parentFirstName")}
          alternativeCodeErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ParentDetails.Person.AlternativeDocumentNumber"
            )?.message
          }
          alternativeTypeErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ParentDetails.Person.AlternativeDocumentTypeId"
            )?.message
          }
          alternativeReasonErrorText={
            errors?.find(
              (e) =>
                e.propertyName ===
                "ParentDetails.Person.AlternativeDocumentReasonId"
            )?.message
          }
          lastNameErrorText={
            errors?.find((e) => e.propertyName === "ParentDetails.LastName")
              ?.message
          }
          isLastNameReadonly={!!showParentOibSuccess}
          firstNameErrorText={
            errors?.find((e) => e.propertyName === "ParentDetails.FirstName")
              ?.message
          }
          isFirstNameReadonly={!!showParentOibSuccess}
        />
        <Row>
          <Col md={6}>
            {showParentOibAlert && (
              <InfoPanel
                color="danger"
                message=""
                dismissible={false}
                fullWidth
              >
                {showParentOibAlert}
              </InfoPanel>
            )}
            {showParentOibSuccess && (
              <InfoPanel
                color="success"
                message=""
                dismissible={false}
                fullWidth
              >
                {showParentOibSuccess}
              </InfoPanel>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={2}>
            <DatePicker
              label={t("common.schoolCandidateModel.dateOfBirth")}
              name="schoolCandidate.dateOfBirth"
              onChange={(e) =>
                handleParentDateOfBirthChange(new Date(e.target.value))
              }
              value={
                item.parent?.dateOfBirth
                  ? new Date(item.parent.dateOfBirth)
                  : null
              }
              minYear={1940}
              disabled={!parentWithoutOib}
              errorText={
                errors?.find(
                  (e) => e.propertyName === "ParentDetails.DateOfBirth"
                )?.message
              }
            />
          </Col>
          <Col md={2}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              name="schoolCandidate.gender"
              value={item.parent?.genderId}
              onChange={(e) =>
                handleParentGenderChange(Number.parseInt(e.target.value))
              }
              selectData={codeBookToSelectData(
                genders?.filter((v) => v.id !== 0)
              )}
              disabled={!parentWithoutOib}
              errorText={
                errors?.find((e) => e.propertyName === "ParentDetails.GenderId")
                  ?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AddressSelect
              title={t("common.schoolCandidateModel.permanentAddress")}
              readOnly={false}
              disabled={!parentWithoutOib}
              value={item.parent?.residence?.permanentAddress}
              handleChange={handleParentPermanentAddressChange}
              errorText={
                errors?.find(
                  (e) =>
                    e.propertyName ===
                    "ParentDetails.Residence.PermanentAddress"
                )?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <AddressSelect
              title={t("common.schoolCandidateModel.temporaryAddress")}
              readOnly={false}
              disabled={!parentWithoutOib}
              value={item.parent?.residence?.temporaryAddress}
              handleChange={handleParentTemporaryAddressChange}
              errorText={
                errors?.find(
                  (e) =>
                    e.propertyName ===
                    "ParentDetails.Residence.TemporaryAddress"
                )?.message
              }
              dropDownDirection="top"
            />
          </Col>
        </Row>
      </FormCard>
      {errors?.find((e) => e.propertyName?.startsWith("ParentChild")) !=
        null && (
        <FormCard>
          <InfoPanel
            color="danger"
            message={t("common.requestModel.noParentChildRelationship")}
            fullWidth
          >
            <Row>
              <Col md={12}>
                {t("common.requestModel.noParentChildRelationship")}
              </Col>
            </Row>
          </InfoPanel>
          <InfoPanel
            color="danger"
            message={t("common.requestModel.noParentChildRelationship")}
            fullWidth
          >
            <Row>
              <Col md={12}>
                <Checkbox
                  label={t("common.requestModel.force")}
                  checked={force}
                  onChange={() => {
                    setForce(!force);
                  }}
                />
              </Col>
            </Row>
          </InfoPanel>
        </FormCard>
      )}
      <ToolbarEdit
        showCancel="show"
        showSave={isParentChildValid() ? "show" : "disable"}
        onClickSave={handleSaveClicked2}
        setSaveErrors={setErrors}
      />
      <Modal
        type="confirm"
        confirmText={t("common.confirm")}
        cancelText={t("common.quit")}
        open={showModal}
        title={t("common.warning")}
        onConfirm={handleEntityWithoutOibChange}
        onClose={handleEntityWithoutOibCancel}
      >
        {t("common.requestModel.noOibWarning")}
      </Modal>
    </ContentArea>
  );
}
